import { useQuery } from "@apollo/client";
import { Autocomplete, Grid, Paper, Stack, TextField, Typography } from "@mui/material";
import { DateField } from "components/Forms/DateField";
import { FormTextField } from "components/Forms/FormTextField";
import { GetWasteShipmentQuery, WasteShipmentStatus } from "generated-graphql/graphql";
import { useTenant } from "hooks/useTenant";
import { UseValidatingFormReturn } from "hooks/useValidatingForm";
import { Controller, FormProvider } from "react-hook-form";

import { NO_VALUE_PLACEHOLDER } from "../Containers/containerDisplay";
import { TENANT_WASTE_VENDOR_OPTIONS_QUERY } from "./api";
import { formatShipmentDate } from "./shipmentDisplay";

type Shipment = NonNullable<GetWasteShipmentQuery["wasteShipment"]>;
type VendorOption = { id: string; name: string; usedByTenant: boolean };

/** The editable half of a shipment: everything the request itself states. */
export type EditShipmentFormValues = {
  vendorId: string | null;
  transporterName: string;
  destinationName: string;
  destinationEpaId: string;
  plannedShipDate: string | null;
  scheduledPickupDate: string | null;
};

export function SectionCard({
  title,
  action,
  children,
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Paper elevation={0} sx={{ p: 3, flexGrow: 1 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        {/* The same overline every pane on this page wears. */}
        <Typography
          component="p"
          variant="overline"
          color="text.secondary"
          sx={{ m: 0, letterSpacing: "1px" }}
        >
          {title}
        </Typography>
        {action}
      </Stack>
      {children}
    </Paper>
  );
}

function FieldValue({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Grid size={{ xs: 12, sm: 6 }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2">{children ?? NO_VALUE_PLACEHOLDER}</Typography>
    </Grid>
  );
}

/**
 * The request's fields once they can no longer change.
 *
 * Deliberately the same card, in the same place, with the same fields in the
 * same order as the editable form -- only the controls are gone. Moving these
 * into the rail when a shipment ships would make the reader re-find them at
 * exactly the moment they start comparing them against a manifest.
 *
 * The facility is not here: it identifies the shipment and is already in the
 * header.
 */
export function ShipmentDetailsFacts({ shipment }: { shipment: Shipment }) {
  // Canceling releases this shipment's containers from the pickup but leaves the
  // date it had arranged. The present tense reads as a commitment someone is
  // still holding, and no container is waiting on it.
  //
  // Only when there is a date to describe: a canceled shipment that never
  // scheduled a pickup would otherwise be labelled as having had one, over an
  // empty value — trading a stale commitment for an invented one.
  const pickupWasAbandoned =
    shipment.status === WasteShipmentStatus.Canceled && shipment.scheduledPickupDate != null;
  const pickupLabel = pickupWasAbandoned ? "Pickup was scheduled" : "Scheduled pickup date";

  return (
    <SectionCard title="Shipment details">
      <Grid container spacing={2}>
        <FieldValue label="Vendor">{shipment.vendor?.name}</FieldValue>
        <FieldValue label="Transporter">{shipment.transporterName}</FieldValue>
        <FieldValue label="Destination (TSDF)">{shipment.destinationName}</FieldValue>
        <FieldValue label="Destination EPA ID">{shipment.destinationEpaId}</FieldValue>
        <FieldValue label="Planned ship date">
          {formatShipmentDate(shipment.plannedShipDate)}
        </FieldValue>
        <FieldValue label={pickupLabel}>
          {formatShipmentDate(shipment.scheduledPickupDate)}
        </FieldValue>
      </Grid>
    </SectionCard>
  );
}

export function ShipmentDetailsFields({
  form,
}: {
  form: UseValidatingFormReturn<EditShipmentFormValues>;
}) {
  const { tenantId } = useTenant();
  const { data: vendorData, loading: vendorsLoading } = useQuery(
    TENANT_WASTE_VENDOR_OPTIONS_QUERY,
    { variables: { tenantId: tenantId ?? "" }, skip: !tenantId }
  );
  const vendorOptions: VendorOption[] = vendorData?.tenant?.wasteVendorOptions ?? [];
  const { control } = form;

  return (
    <SectionCard title="Shipment details">
      <FormProvider {...form}>
        {/*
          Editable fields only. The facility, the shipped date and the total are
          all read-only and already on the page -- in the header, the progress
          rail and the stat strip -- so repeating them here would just be a
          second, staler copy sitting inside a form.
        */}
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Controller
              control={control}
              name="vendorId"
              render={({ field }) => (
                <Autocomplete
                  options={vendorOptions}
                  getOptionLabel={(o: VendorOption) => o.name}
                  groupBy={(o: VendorOption) => (o.usedByTenant ? "Your vendors" : "All vendors")}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  value={vendorOptions.find((o) => o.id === field.value) ?? null}
                  onChange={(_e, value) => field.onChange(value?.id ?? null)}
                  loading={vendorsLoading}
                  fullWidth
                  renderInput={(params) => <TextField {...params} label="Vendor" size="small" />}
                  noOptionsText="No vendors found in this organization's waste data"
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormTextField
              control={control}
              name="transporterName"
              label="Transporter"
              fullWidth
              textFieldProps={{ size: "small" }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormTextField
              control={control}
              name="destinationName"
              label="Destination (TSDF)"
              fullWidth
              textFieldProps={{ size: "small" }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormTextField
              control={control}
              name="destinationEpaId"
              label="Destination EPA ID"
              fullWidth
              textFieldProps={{ size: "small" }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <DateField
              control={control}
              name="plannedShipDate"
              label="Planned ship date"
              datePickerProps={{ slotProps: { textField: { size: "small", fullWidth: true } } }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <DateField
              control={control}
              name="scheduledPickupDate"
              label="Scheduled pickup date"
              helperText="Flags this shipment's containers as awaiting pickup"
              datePickerProps={{ slotProps: { textField: { size: "small", fullWidth: true } } }}
            />
          </Grid>
        </Grid>
      </FormProvider>
    </SectionCard>
  );
}
